"use client";

import Image from "next/image";
import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { getEbooks } from "@/data/ebooks";
import { HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from "@/data/hubspot-forms";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import { metadataBaseUrl } from "@/lib/metadata";

type Props = {
  locale: string;
  slug: string;
};

export default function EbookDetailPage({ locale, slug }: Props) {
  const t = useTranslations("ebooks.detail");
  const tData = useTranslations("ebooksData");
  const normalizedLocale = locale?.startsWith("es") ? "es" : "en";
  const ebooks = useMemo(() => getEbooks((s) => tData.raw(s)), [tData]);
  const ebook = ebooks.find((e) => e.slug === slug);

  if (!ebook) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: ebook.title,
    description: ebook.description,
    image: ebook.cover.startsWith("http") ? ebook.cover : `${metadataBaseUrl}${ebook.cover}`,
    inLanguage: normalizedLocale,
    offers: {
      "@type": "Offer",
      url: `${metadataBaseUrl}/ebooks-gratuitos/${ebook.slug}`,
      price: "0",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
    },
    author: {
      "@type": "Organization",
      name: "Geesol Renovables",
    },
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-surface-dark-raised dark:via-surface-dark-raised dark:to-surface-dark text-gray-900 dark:text-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <Link
          href="/ebooks-gratuitos"
          className="inline-flex items-center text-sm font-semibold text-red-600 dark:text-red-300 hover:text-red-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="h-4 w-4 mr-2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {t("back")}
        </Link>

        <div className="grid gap-10 lg:gap-12 mt-8 lg:grid-cols-12 items-start">
          <div className="lg:col-span-4 space-y-4">
            <p className="text-sm uppercase tracking-eyebrow text-red-600 dark:text-red-300 font-semibold">
              {t("eyebrow")}
            </p>
            <h1 className="type-h1">{ebook.title}</h1>
            <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300">
              {ebook.description}
            </p>

            {ebook.highlights && ebook.highlights.length > 0 && (
              <div className="space-y-3 mt-6">
                <h2 className="text-sm font-semibold uppercase tracking-eyebrow text-gray-600 dark:text-gray-300">
                  {t("highlights")}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {ebook.highlights.map((highlight, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-full bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-100 px-3 py-1 text-xs font-semibold"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-4 order-first lg:order-none">
            <div className="relative mx-auto w-full max-w-md">
              <div className="absolute -inset-6 rounded-card bg-[radial-gradient(circle_at_30%_30%,rgba(208,27,23,0.14),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(12,59,82,0.12),transparent_40%)] blur-xl" />
              <div className="relative rounded-card bg-white/80 dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-800 shadow-card-lg dark:shadow-card-dark p-6">
                <div className="relative h-96 w-full">
                  <Image
                    src={ebook.cover}
                    alt={ebook.title}
                    fill
                    className="object-contain rounded-2xl drop-shadow-2xl"
                    sizes="(max-width: 1024px) 80vw, 360px"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-card bg-white/90 dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-800 shadow-card-lg dark:shadow-card-dark p-5 sm:p-6 min-w-[320px] max-w-lg mx-auto lg:ml-auto">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {t("download")}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {t("formNote")}
              </p>
              <HubSpotForm
                portalId={HUBSPOT_PORTAL_ID}
                formId={ebook.formId}
                region={HUBSPOT_REGION}
              />
            </div>
          </div>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </main>
  );
}
